// Ported from RaceCut (lib/racecut/aac.ts). Keep the two in step when fixing
// a drawing or encoding bug; the export and the preview both depend on it.
import { ArrayBufferTarget, Muxer } from "mp4-muxer";
import type { PcmAudio } from "./audio";

export type AacStats = { chunks: number; described: number };

const BITRATE = 192_000;
const FRAMES_PER_PACKET = 4096;
/** The AAC-LC priming every common encoder (Chrome, Safari) adds: 2112 samples. */
const ENCODER_DELAY = 2112;
const yieldToLoop = () => new Promise<void>((settle) => setTimeout(settle, 0));

function aacConfig(
  sampleRate: number,
  numberOfChannels: number,
): AudioEncoderConfig {
  return { codec: "mp4a.40.2", sampleRate, numberOfChannels, bitrate: BITRATE };
}

export async function canEncodeAac(
  sampleRate = 48_000,
  channels = 2,
): Promise<boolean> {
  if (typeof AudioEncoder === "undefined") return false;
  const support = await AudioEncoder.isConfigSupported(
    aacConfig(sampleRate, channels),
  );
  return support.supported === true;
}

/** AAC-LC AudioSpecificConfig (2 bytes): objectType=2, sampling-frequency index, channel config. */
function audioSpecificConfig(sampleRate: number, channels: number): Uint8Array {
  const rates = [
    96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025,
    8000, 7350,
  ];
  let idx = rates.indexOf(sampleRate);
  if (idx < 0) idx = 3; // 48 kHz
  const objectType = 2;
  return new Uint8Array([
    (objectType << 3) | (idx >> 1),
    ((idx & 1) << 7) | (channels << 3),
  ]);
}

export async function encodeAac(
  audio: PcmAudio,
  muxer: Muxer<ArrayBufferTarget>,
): Promise<AacStats> {
  if (typeof AudioEncoder === "undefined")
    throw new Error("WebCodecs AudioEncoder is not available");
  const channels = audio.channels.length;
  const config = aacConfig(audio.sampleRate, channels);
  const support = await AudioEncoder.isConfigSupported(config);
  if (!support.supported)
    throw new Error("AAC encoding not supported by this browser");
  let err: Error | null = null;
  // Safari's AAC encoder often omits decoderConfig.description (the AudioSpecificConfig);
  // without it the mp4 has an AAC track players can't decode → silent video. Synthesize it.
  const asc = audioSpecificConfig(audio.sampleRate, channels);
  const stats: AacStats = { chunks: 0, described: 0 };
  const enc = new AudioEncoder({
    output: (chunk, meta) => {
      stats.chunks++;
      const dc = meta?.decoderConfig;
      if (dc?.description) stats.described++;
      const fixed: EncodedAudioChunkMetadata =
        dc && !dc.description
          ? { ...meta, decoderConfig: { ...dc, description: asc } }
          : (meta ?? {
              decoderConfig: {
                codec: config.codec,
                sampleRate: config.sampleRate,
                numberOfChannels: config.numberOfChannels,
                description: asc,
              },
            });
      if (!fixed.decoderConfig)
        fixed.decoderConfig = {
          codec: config.codec,
          sampleRate: config.sampleRate,
          numberOfChannels: config.numberOfChannels,
          description: asc,
        };
      muxer.addAudioChunk(chunk, fixed);
    },
    error: (e) => {
      err = e;
    },
  });
  enc.configure(config);
  const total = audio.channels[0].length;
  // AAC encoders put ENCODER_DELAY samples of priming in front of the audio
  // and the MP4 we write has no edit list to hide it, so everything played
  // 44 ms late (measured: a duet acapella against its own voice tracks, and
  // the same lag on studio exports against their picture). Feeding the
  // encoder from that far in cancels it; the samples skipped are the
  // first 44 ms, which the mixdown fades in anyway.
  const skip = Math.min(ENCODER_DELAY, Math.max(0, total - 1));
  for (let off = skip; off < total; off += FRAMES_PER_PACKET) {
    const n = Math.min(FRAMES_PER_PACKET, total - off);
    const data = new Float32Array(n * channels);
    for (let c = 0; c < channels; c++)
      data.set(audio.channels[c].subarray(off, off + n), c * n);
    const ad = new AudioData({
      format: "f32-planar",
      sampleRate: audio.sampleRate,
      numberOfFrames: n,
      numberOfChannels: channels,
      timestamp: Math.round(((off - skip) / audio.sampleRate) * 1_000_000),
      data,
    });
    enc.encode(ad);
    ad.close();
    if (enc.encodeQueueSize > 16) await yieldToLoop();
  }
  await enc.flush();
  enc.close();
  if (err) throw err;
  return stats;
}

/** An audio-only MP4 (.m4a) carrying `audio` as AAC. */
export async function m4aOf(audio: PcmAudio): Promise<ArrayBuffer> {
  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    audio: {
      codec: "aac",
      sampleRate: audio.sampleRate,
      numberOfChannels: audio.channels.length,
    },
    fastStart: "in-memory",
  });
  await encodeAac(audio, muxer);
  muxer.finalize();
  return muxer.target.buffer;
}
